/**
 * ConvertToProposalButton — last-mile UX between a saved calc quotation and
 * a CRM proposal row.
 *
 * Plan 2 Phase 4.1 (originally rendered by CalcWizard; now rendered
 * directly by each 1:1-port calculator page after `onSaveQuotation`
 * returns an id). Opens a confirm modal asking for project name +
 * optional notes, then POSTs to /api/v1/proposals via the typed
 * `proposalsApi.createFromQuotation` client.
 *
 * On success: shows a transient toast and either fires `onSuccess` or
 * navigates to /proposals/:id/edit (default — keeps the rep moving down
 * the funnel without manual clicks).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2, X } from 'lucide-react';
import { proposalsApi, type CalculatorKey } from '@/api/calculators';

export interface ConvertToProposalButtonProps {
  /** Matches backend enum. Cast at the call site since the wizard's
   *  calculatorKey is just a string — we narrow here for the API call. */
  calculatorKey: string;
  quotationId: string;
  /** Used to pre-fill the project-name field. Falls back to "Customer". */
  customerName?: string;
  /** When set, the proposal is attached to this lead. */
  leadId?: string | null;
  /** Optional callback. If omitted, default behaviour is to navigate to
   *  /proposals/:id/edit on success. */
  onSuccess?: (proposal: { id: string; proposalNumber: string }) => void;
}

export function ConvertToProposalButton(props: ConvertToProposalButtonProps) {
  const { calculatorKey, quotationId, customerName, leadId, onSuccess } = props;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [projectName, setProjectName] = useState(
    `${prettyCalcLabel(calculatorKey)} — ${customerName ?? 'Customer'}`
  );
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    setError(null);

    if (!projectName.trim()) {
      setError('Project name is required.');
      return;
    }
    setSubmitting(true);
    try {
      const proposal = await proposalsApi.createFromQuotation({
        calculatorKey: calculatorKey as CalculatorKey,
        quotationId,
        projectName: projectName.trim(),
        leadId: leadId ?? null,
        notes: notes.trim() || null,
      });
      setToast(`Proposal ${proposal.proposalNumber} created`);
      window.setTimeout(() => setToast(null), 3500);
      setOpen(false);
      if (onSuccess) {
        onSuccess(proposal);
      } else {
        navigate(`/proposals/${proposal.id}/edit`);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not create proposal.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="convert-to-proposal-trigger"
        style={{
          padding: '10px 18px',
          background: '#0066b3',
          color: 'white',
          border: 'none',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 14,
          fontWeight: 600,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          alignSelf: 'flex-start',
        }}
      >
        Convert to Proposal <ArrowRight size={14} />
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Convert to Proposal"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={(e) => {
            // Click on backdrop closes — guard against clicks inside the panel
            if (e.target === e.currentTarget && !submitting) {
              setOpen(false);
            }
          }}
        >
          <form
            onSubmit={handleSubmit}
            style={{
              background: 'white',
              borderRadius: 8,
              padding: 22,
              width: 440,
              maxWidth: '92vw',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>Convert to Proposal</h2>
              <button
                type="button"
                aria-label="Close"
                onClick={() => !submitting && setOpen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  display: 'flex',
                }}
              >
                <X size={18} />
              </button>
            </div>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              <span style={{ color: '#444', fontWeight: 500 }}>Project name *</span>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                disabled={submitting}
                data-testid="convert-modal-project-name"
                style={{
                  padding: '8px 10px',
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  fontSize: 14,
                }}
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              <span style={{ color: '#444', fontWeight: 500 }}>Notes (optional)</span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={submitting}
                rows={3}
                data-testid="convert-modal-notes"
                style={{
                  padding: '8px 10px',
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  fontSize: 14,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                }}
              />
            </label>

            {error && (
              <div
                role="alert"
                style={{
                  padding: 10,
                  background: '#fde7e7',
                  color: '#8b2222',
                  borderRadius: 4,
                  fontSize: 13,
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
              <button
                type="button"
                onClick={() => !submitting && setOpen(false)}
                disabled={submitting}
                style={{
                  padding: '8px 14px',
                  background: 'white',
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                data-testid="convert-modal-submit"
                style={{
                  padding: '8px 14px',
                  background: '#0066b3',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: submitting ? 'wait' : 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
                Create Proposal
              </button>
            </div>
          </form>
        </div>
      )}

      {toast && (
        <div
          role="status"
          style={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 1100,
            padding: '10px 14px',
            background: '#eaf6ea',
            color: '#2a7a2a',
            border: '1px solid #b7dcb7',
            borderRadius: 6,
            fontSize: 14,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          {toast}
        </div>
      )}
    </>
  );
}

// Small helper — surfaces a friendly label for the seeded project name when
// the wizard didn't pass a `title` through. Falls back to the raw key.
function prettyCalcLabel(key: string): string {
  switch (key) {
    case 'ss_tank':
      return 'SS Tank';
    case 'domestic_heat_pump':
      return 'Domestic Heat Pump';
    case 'commercial_heat_pump':
      return 'Commercial Heat Pump';
    case 'pool_heat_pump':
      return 'Pool Heat Pump';
    default:
      return key;
  }
}
